import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { useGetCategoriesQuery, useDeleteCategoryMutation, useDeletePhaseMutation, useDeleteStepMutation } from '../../Services/PathwayAdminAPI';
import CategoryForm from './CategoryManagement/CategoryForm';
import PhaseForm from './PhaseManagement/PhaseForm';
import StepForm from './StepManagement/StepForm';
import ExerciseBuilder from './ExerciseManagement/ExerciseBuilder';
import StepPreview from './StepManagement/StepPreview';
import PathwayAnalytics from './Analytics/PathwayAnalytics';
import '../../Styles/PathwayDashboard.css';

const PathwayDashboard = () => {
  const { data: categoriesData, isLoading, error, refetch } = useGetCategoriesQuery();
  const [deleteCategory] = useDeleteCategoryMutation();
  const [deletePhase] = useDeletePhaseMutation();
  const [deleteStep] = useDeleteStepMutation();
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedPhases, setExpandedPhases] = useState({});
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showPhaseForm, setShowPhaseForm] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [preselectedCategoryId, setPreselectedCategoryId] = useState(null);
  const [showStepForm, setShowStepForm] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  const [preselectedPhaseId, setPreselectedPhaseId] = useState(null);
  const [showExerciseBuilder, setShowExerciseBuilder] = useState(false);
  const [selectedStepForExercises, setSelectedStepForExercises] = useState(null);
  const [showStepPreview, setShowStepPreview] = useState(false);
  const [selectedStepForPreview, setSelectedStepForPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('management'); // 'management' or 'analytics'

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const togglePhase = (phaseId) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };

  const handleNewCategory = () => {
    setSelectedCategory(null);
    setShowCategoryForm(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setShowCategoryForm(true);
  };

  const handleCloseCategoryForm = () => {
    setShowCategoryForm(false);
    setSelectedCategory(null);
  };

  const handleCategorySuccess = () => {
    // The query will automatically refetch due to cache invalidation
    setShowCategoryForm(false);
    setSelectedCategory(null);
  };

  const handleNewPhase = (categoryId) => {
    setSelectedPhase(null);
    setPreselectedCategoryId(categoryId);
    setShowPhaseForm(true);
  };

  const handleEditPhase = (phase) => {
    setSelectedPhase(phase);
    setPreselectedCategoryId(null);
    setShowPhaseForm(true);
  };

  const handleClosePhaseForm = () => {
    setShowPhaseForm(false);
    setSelectedPhase(null);
    setPreselectedCategoryId(null);
  };

  const handlePhaseSuccess = () => {
    // The query will automatically refetch due to cache invalidation
    setShowPhaseForm(false);
    setSelectedPhase(null);
    setPreselectedCategoryId(null);
    // Force refetch to ensure UI updates with new phase
    refetch();
  };

  const handleNewStep = (phaseId) => {
    setSelectedStep(null);
    setPreselectedPhaseId(phaseId);
    setShowStepForm(true);
  };

  const handleEditStep = (step) => {
    setSelectedStep(step);
    setPreselectedPhaseId(null);
    setShowStepForm(true);
  };

  const handleCloseStepForm = () => {
    setShowStepForm(false);
    setSelectedStep(null);
    setPreselectedPhaseId(null);
  };

  const handleStepSuccess = () => {
    // The query will automatically refetch due to cache invalidation
    setShowStepForm(false);
    setSelectedStep(null);
    setPreselectedPhaseId(null);
    // Force refetch to ensure UI updates with new step
    refetch();
  };

  const handleManageExercises = (step) => {
    setSelectedStepForExercises(step);
    setShowExerciseBuilder(true);
  };

  const handleCloseExerciseBuilder = () => {
    setShowExerciseBuilder(false);
    setSelectedStepForExercises(null);
  };

  const handlePreviewStep = (step) => {
    setSelectedStepForPreview(step);
    setShowStepPreview(true);
  };

  const handleCloseStepPreview = () => {
    setShowStepPreview(false);
    setSelectedStepForPreview(null);
  };

  const handleDeleteStep = async (step) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete the step "${step.title}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#FD9C00',
      cancelButtonColor: '#6c757d',
    });

    if (result.isConfirmed) {
      try {
        await deleteStep(step.id).unwrap();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Step has been deleted successfully.',
          timer: 2000,
          showConfirmButton: false,
          confirmButtonColor: '#FD9C00',
        });
        // Force refetch to ensure UI updates
        refetch();
      } catch (error) {
        console.error('Error deleting step:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.data?.message || 'Failed to delete step. Please try again.',
          confirmButtonColor: '#FD9C00',
        });
      }
    }
  };

  const handleDeletePhase = async (phase) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete the phase "${phase.name}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#FD9C00',
      cancelButtonColor: '#6c757d',
    });

    if (result.isConfirmed) {
      try {
        await deletePhase(phase.id).unwrap();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Phase has been deleted successfully.',
          timer: 2000,
          showConfirmButton: false,
          confirmButtonColor: '#FD9C00',
        });
        // Force refetch to ensure UI updates
        refetch();
      } catch (error) {
        console.error('Error deleting phase:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.data?.message || 'Failed to delete phase. Please try again.',
          confirmButtonColor: '#FD9C00',
        });
      }
    }
  };

  const handleDeleteCategory = async (category) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete the category "${category.name}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#FD9C00',
      cancelButtonColor: '#6c757d',
    });

    if (result.isConfirmed) {
      try {
        await deleteCategory(category.id).unwrap();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Category has been deleted successfully.',
          timer: 2000,
          showConfirmButton: false,
          confirmButtonColor: '#FD9C00',
        });
      } catch (error) {
        console.error('Error deleting category:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.data?.message || 'Failed to delete category. Please try again.',
          confirmButtonColor: '#FD9C00',
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="pathway-dashboard">
        <div className="loading-spinner">Loading pathway data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pathway-dashboard">
        <div className="error-message">
          Error loading pathway data: {error.message || 'Unknown error'}
        </div>
      </div>
    );
  }

  const categories = categoriesData?.data || [];

  return (
    <div className="pathway-dashboard">
      <div className="pathway-tabs">
        <button 
          className={`tab-button ${activeTab === 'management' ? 'active' : ''}`}
          onClick={() => setActiveTab('management')}
        >
          <i className="fas fa-cog"></i> Management
        </button>
        <button 
          className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          <i className="fas fa-chart-line"></i> Analytics
        </button>
      </div>

      {activeTab === 'analytics' ? (
        <PathwayAnalytics />
      ) : (
        <>
          <h3>Pathway Management</h3>
          <div className="pathway-dashboard-wrapper">
            <div className="dashboard-header">
              <div className="dashboard-header-left">
                <h4>Pathway Management</h4>
                <p className="count">{categories.length} categories</p>
              </div>
              <button className="btn-new-category" onClick={handleNewCategory}>
                <i className="fas fa-plus"></i> New Category
              </button>
            </div>

        <div className="pathway-tree">
          {categories.length === 0 ? (
            <div className="empty-state">
              <p>No categories found. Create your first category to get started.</p>
            </div>
          ) : (
            categories.map(category => (
            <div key={category.id} className="category-item">
              <div className="category-header">
                <div className="category-info" onClick={() => toggleCategory(category.id)} style={{ cursor: 'pointer' }}>
                  <button 
                    className="expand-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategory(category.id);
                    }}
                  >
                    <i className={`fas fa-chevron-${expandedCategories[category.id] ? 'down' : 'right'}`}></i>
                  </button>
                  <h3>{category.name}</h3>
                  <span className={`status-badge ${category.is_published ? 'published' : 'draft'}`}>
                    {category.is_published ? 'Published' : 'Draft'}
                  </span>
                  <span className="count-badge">
                    {category.phases?.length || 0} phases
                  </span>
                </div>
                <div className="action-buttons">
                  <button className="btn-outline" onClick={(e) => {
                    e.stopPropagation();
                    handleEditCategory(category);
                  }}>
                    Edit
                  </button>
                  <button className="btn-outline" onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCategory(category);
                  }}>
                    Delete
                  </button>
                </div>
              </div>

              {expandedCategories[category.id] && (
                <div className="phases-container">
                  <div className="phases-header">
                    <button 
                      className="btn-new-phase" 
                      onClick={() => handleNewPhase(category.id)}
                    >
                      <i className="fas fa-plus"></i> New Phase
                    </button>
                  </div>
                  {category.phases && category.phases.length > 0 ? (
                    category.phases.map(phase => (
                      <div key={phase.id} className="phase-item">
                        <div className="phase-header">
                          <div className="phase-info" onClick={() => togglePhase(phase.id)} style={{ cursor: 'pointer' }}>
                            <button 
                              className="expand-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                togglePhase(phase.id);
                              }}
                            >
                              <i className={`fas fa-chevron-${expandedPhases[phase.id] ? 'down' : 'right'}`}></i>
                            </button>
                            <h4>Phase {phase.phase_number}: {phase.name}</h4>
                            <span className="count-badge">
                              {phase.steps?.length || 0} steps
                            </span>
                          </div>
                          <div className="action-buttons">
                            <button className="btn-outline" onClick={(e) => {
                              e.stopPropagation();
                              handleEditPhase(phase);
                            }} title="Edit">
                              Edit
                            </button>
                            <button className="btn-outline" onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePhase(phase);
                            }} title="Delete">
                              Delete
                            </button>
                          </div>
                        </div>

                        {expandedPhases[phase.id] && (
                          <div className="steps-container">
                            <div className="steps-header">
                              <button 
                                className="btn-new-step" 
                                onClick={() => handleNewStep(phase.id)}
                              >
                                <i className="fas fa-plus"></i> New Step
                              </button>
                            </div>
                            {phase.steps && phase.steps.length > 0 ? (
                              phase.steps.map(step => (
                                <div key={step.id} className="step-item">
                                  <div className="step-info">
                                    <span className="step-number">Step {step.step_number}</span>
                                    <span className="step-title">{step.title}</span>
                                    {step.tags && step.tags.length > 0 && (
                                      <div className="step-tags">
                                        {step.tags.map((tag, idx) => (
                                          <span key={idx} className="tag-badge">{tag}</span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="action-buttons">
                                    <button className="btn-outline" onClick={(e) => {
                                      e.stopPropagation();
                                      handlePreviewStep(step);
                                    }} title="Preview Step">
                                      <i className="fas fa-eye"></i> Preview
                                    </button>
                                    <button className="btn-outline" onClick={(e) => {
                                      e.stopPropagation();
                                      handleManageExercises(step);
                                    }} title="Manage Exercises">
                                      <i className="fas fa-clipboard-list"></i> Exercises
                                    </button>
                                    <button className="btn-outline" onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditStep(step);
                                    }} title="Edit">
                                      Edit
                                    </button>
                                    <button className="btn-outline" onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteStep(step);
                                    }} title="Delete">
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="empty-state-small">
                                No steps in this phase
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="empty-state-small">
                      No phases in this category
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
        </div>
      </div>

      <CategoryForm
        show={showCategoryForm}
        onHide={handleCloseCategoryForm}
        category={selectedCategory}
        onSuccess={handleCategorySuccess}
      />

      <PhaseForm
        show={showPhaseForm}
        onHide={handleClosePhaseForm}
        phase={selectedPhase}
        onSuccess={handlePhaseSuccess}
        preselectedCategoryId={preselectedCategoryId}
      />

      <StepForm
        show={showStepForm}
        onHide={handleCloseStepForm}
        step={selectedStep}
        onSuccess={handleStepSuccess}
        preselectedPhaseId={preselectedPhaseId}
      />

      <ExerciseBuilder
        show={showExerciseBuilder}
        onHide={handleCloseExerciseBuilder}
        stepId={selectedStepForExercises?.id}
      />

      <StepPreview
        show={showStepPreview}
        onHide={handleCloseStepPreview}
        stepId={selectedStepForPreview?.id}
      />
        </>
      )}
    </div>
  );
};

export default PathwayDashboard;
